import { SAUCES, applySauce, currentSauce, type SauceId } from './sauces'
import { h } from './ui'

/**
 * A listbox rather than a <select>, so each option can preview its own palette.
 * The swatch adopts a sauce by setting `data-sauce` on itself — the palette
 * blocks in styles.css aren't scoped to :root, so the custom properties cascade.
 */
export function saucePicker(onChange: () => void): HTMLElement {
  const menu = h('div', { class: 'sauce-menu', role: 'listbox', 'aria-label': 'Colour scheme', hidden: true })

  const buttonSwatch = swatch(currentSauce())
  const buttonLabel = h('span', {}, [labelFor(currentSauce())])
  const button = h(
    'button',
    {
      class: 'control',
      type: 'button',
      'aria-haspopup': 'listbox',
      'aria-expanded': 'false',
      title: 'Colour scheme',
      onclick: () => (menu.hidden ? open() : close()),
      onkeydown: (event: Event) => {
        const key = (event as KeyboardEvent).key
        if (key === 'ArrowDown' || key === 'ArrowUp') {
          event.preventDefault()
          open()
        }
      },
    },
    [buttonSwatch, buttonLabel],
  )

  const options = SAUCES.map((sauce) => {
    const option = h(
      'button',
      {
        class: 'sauce-option',
        type: 'button',
        role: 'option',
        'aria-selected': String(sauce.id === currentSauce()),
        onclick: () => choose(sauce.id),
      },
      [
        swatch(sauce.id),
        h('span', { class: 'name' }, [sauce.label]),
        h('span', { class: 'hint' }, [sauce.hint]),
      ],
    )
    return option
  })
  menu.append(...options)

  const root = h('div', { class: 'sauce' }, [button, menu])

  function labelFor(id: SauceId): string {
    return SAUCES.find((sauce) => sauce.id === id)?.label ?? id
  }

  function swatch(id: SauceId): HTMLElement {
    return h('span', { class: 'swatch', 'data-sauce': id, 'aria-hidden': 'true' }, [
      h('i', { class: 'a' }),
      h('i', { class: 'b' }),
      h('i', { class: 'c' }),
    ])
  }

  function onDocumentPointerDown(event: Event): void {
    if (!menu.isConnected) {
      document.removeEventListener('pointerdown', onDocumentPointerDown)
      return
    }
    if (!root.contains(event.target as Node)) close()
  }

  function onDocumentKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      close()
      button.focus()
      return
    }
    if (menu.hidden) return
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return
    event.preventDefault()
    const focused = options.indexOf(document.activeElement as HTMLButtonElement)
    const step = event.key === 'ArrowDown' ? 1 : -1
    const next = (focused + step + options.length) % options.length
    options[next]?.focus()
  }

  function open(): void {
    menu.hidden = false
    button.setAttribute('aria-expanded', 'true')
    document.addEventListener('pointerdown', onDocumentPointerDown)
    document.addEventListener('keydown', onDocumentKeyDown)
    options.find((option) => option.getAttribute('aria-selected') === 'true')?.focus()
  }

  function close(): void {
    menu.hidden = true
    button.setAttribute('aria-expanded', 'false')
    document.removeEventListener('pointerdown', onDocumentPointerDown)
    document.removeEventListener('keydown', onDocumentKeyDown)
  }

  function choose(id: SauceId): void {
    applySauce(id)
    buttonSwatch.dataset.sauce = id
    buttonLabel.textContent = labelFor(id)
    for (const [index, option] of options.entries()) {
      option.setAttribute('aria-selected', String(SAUCES[index]?.id === id))
    }
    close()
    button.focus()
    onChange()
  }

  return root
}
